import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import EmailSchedulePanel from '../components/EmailSchedulePanel';
import EmailCalendarView from '../components/EmailCalendarView';
import { scheduledEmailTypeAPI, emailTemplateAPI } from '../services/api';

/**
 * ScheduledEmails - Unified page for managing scheduled email notifications and email types
 * Two main tabs:
 * 1. Scheduled Emails - List and calendar views of notifications with management
 * 2. Email Types - Blueprints for email notifications (formerly EmailTypeManagement page)
 */
const ScheduledEmails = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state from URL - now includes dashboard, list, calendar, types
  const activeTab = searchParams.get('tab') || 'dashboard';
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  // Shared state
  const [error, setError] = useState(null);

  // Data state for calendar view
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Email Types state
  const [emailTypes, setEmailTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [cascadePreview, setCascadePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Notification viewer state for email types
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('pending');
  const [regenerating, setRegenerating] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(true);

  // Event selection state for generating notifications
  const [showEventSelector, setShowEventSelector] = useState(false);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [generatingEvents, setGeneratingEvents] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [generationResults, setGenerationResults] = useState(null);

  // Delete confirmation state for email types
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  // Full list view state for all notifications
  const [allNotifications, setAllNotifications] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPagination, setListPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [listFilters, setListFilters] = useState({
    status: 'pending',
    emailTypeId: '',
    eventType: '',
    eventId: '',
    search: '',
    fromDate: '',
    toDate: ''
  });
  const [uniqueEvents, setUniqueEvents] = useState([]); // For event filter dropdown
  const [statusCounts, setStatusCounts] = useState({});
  const [listSort, setListSort] = useState({ field: 'scheduledFor', order: 'asc' }); // Sorting state for list view

  // Form state for editing/creating email types
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    category: 'member',
    offsetDays: 0,
    sendTime: '09:00',
    templateId: '',
    description: '',
    isActive: true,
    recipientConfig: {
      toAddresses: [],
      ccAddresses: [],
      bccAddresses: [],
      includeProshopContact: false,
      includeTournamentPlayers: false,
      includeWaitlist: false,
      includeAdminContact: false
    }
  });

  // Event type options for filtering
  const eventTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'Weekday Event', label: 'Weekday' },
    { value: 'Weekend Event', label: 'Weekend' },
    { value: 'Major Tournament', label: 'Major' },
    { value: 'SCGA Team Event', label: 'SCGA' },
    { value: 'Board Meeting', label: 'Board' },
    { value: 'Course Maintenance', label: 'Maintenance' },
    { value: 'Other', label: 'Other' }
  ];

  // Filter events by type
  const filteredEvents = eventTypeFilter === 'all'
    ? availableEvents
    : availableEvents.filter(event => event.eventType === eventTypeFilter);

  // Fetch data for calendar view - uses NEW ScheduledNotification collection
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [pendingRes, historyRes] = await Promise.all([
        scheduledEmailTypeAPI.getAllNotifications({ status: 'pending', limit: 200 }),
        scheduledEmailTypeAPI.getAllNotifications({ status: 'sent', limit: 50 })
      ]);

      setPendingNotifications(pendingRes.data.notifications || []);
      setNotificationHistory(historyRes.data.notifications || []);
    } catch (err) {
      console.error('Error fetching email schedule data:', err);
      setError(err.message || 'Failed to load email schedule data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load email types data
  const loadTypesData = useCallback(async () => {
    try {
      setTypesLoading(true);
      setError(null);

      const [typesRes, templatesRes] = await Promise.all([
        scheduledEmailTypeAPI.getAll({ activeOnly: false }),
        emailTemplateAPI.getAll()
      ]);

      setEmailTypes(typesRes.data);
      setTemplates(templatesRes.data?.data || templatesRes.data || []);
    } catch (err) {
      console.error('Error loading email types:', err);
      setError('Failed to load email types');
    } finally {
      setTypesLoading(false);
    }
  }, []);

  // Fetch all notifications for full list view
  const fetchAllNotifications = useCallback(async (page = 1) => {
    try {
      setListLoading(true);
      setError(null);

      const params = {
        page,
        limit: 25,
        status: listFilters.status === 'all' ? undefined : listFilters.status,
        emailTypeId: listFilters.emailTypeId || undefined,
        eventType: listFilters.eventType || undefined,
        eventId: listFilters.eventId || undefined,
        search: listFilters.search || undefined,
        fromDate: listFilters.fromDate || undefined,
        toDate: listFilters.toDate || undefined,
        sortBy: listSort.field,
        sortOrder: listSort.order
      };

      const response = await scheduledEmailTypeAPI.getAllNotifications(params);
      const notifications = response.data.notifications || [];
      setAllNotifications(notifications);
      setListPagination({
        page: response.data.pagination?.page || 1,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 0
      });
      setStatusCounts(response.data.statusCounts || {});

      // Extract unique events for the filter dropdown (only on first load or when no filter applied)
      if (page === 1 && !listFilters.eventId) {
        const eventsMap = new Map();
        notifications.forEach(n => {
          if (n.eventId && n.eventName) {
            eventsMap.set(n.eventId, { id: n.eventId, name: n.eventName, type: n.eventType });
          }
        });
        // Also try to get from response if available
        if (response.data.uniqueEvents) {
          response.data.uniqueEvents.forEach(e => eventsMap.set(e.id || e._id, e));
        }
        setUniqueEvents(Array.from(eventsMap.values()).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setListLoading(false);
    }
  }, [listFilters, listSort]);

  // Handle preview notification - navigate to edit page by notification ID
  const handlePreviewNotification = async (notification) => {
    const notificationId = notification._id;

    if (notificationId) {
      navigate(`/scheduled-emails/edit/${notificationId}`);
    } else {
      console.error('Missing notification ID:', notification);
      setError('Cannot open notification details - missing notification ID');
    }
  };

  // Effect for calendar data
  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarData();
    }
  }, [activeTab, fetchCalendarData]);

  // Effect for list data
  useEffect(() => {
    if (activeTab === 'list') {
      fetchAllNotifications();
    }
  }, [activeTab, fetchAllNotifications]);

  // Effect for email types data - also load types for list filter dropdown
  useEffect(() => {
    if (activeTab === 'types' || activeTab === 'list') {
      loadTypesData();
    }
  }, [activeTab, loadTypesData]);

  // Calendar view handlers - using NEW ScheduledNotification system with notificationId
  const handlePreview = async (notificationId) => {
    try {
      // For now, log the preview request - preview can be implemented later
      console.log('Preview notification:', notificationId);
      // TODO: Implement preview endpoint in new system if needed
    } catch (err) {
      console.error('Error previewing notification:', err);
    }
  };

  const handleSkip = async (notificationId) => {
    try {
      await scheduledEmailTypeAPI.skipNotification(notificationId, 'Manually skipped by user', 'admin');
      await fetchCalendarData();
    } catch (err) {
      console.error('Error skipping notification:', err);
      setError(err.message || 'Failed to skip notification');
    }
  };

  const handleUnskip = async (notificationId) => {
    try {
      await scheduledEmailTypeAPI.unskipNotification(notificationId, 'admin');
      await fetchCalendarData();
    } catch (err) {
      console.error('Error re-enabling notification:', err);
      setError(err.message || 'Failed to re-enable notification');
    }
  };

  const handleTrigger = async (notificationId) => {
    try {
      // Update status to trigger immediate send - the backend cron will pick it up
      await scheduledEmailTypeAPI.updateNotification(notificationId, {
        scheduledFor: new Date().toISOString(),
        status: 'pending'
      });
      await fetchCalendarData();
    } catch (err) {
      console.error('Error triggering notification:', err);
      setError(err.message || 'Failed to trigger notification');
    }
  };

  // Email type handlers
  const handleEditClick = async (type) => {
    setSelectedType(type);
    setFormData({
      code: type.code || '',
      name: type.name,
      category: type.category || 'member',
      offsetDays: type.offsetDays,
      sendTime: type.sendTime || '09:00',
      templateId: type.templateId?._id || type.templateId || '',
      description: type.description || '',
      isActive: type.isActive !== false,
      recipientConfig: {
        toAddresses: type.recipientConfig?.toAddresses || [],
        ccAddresses: type.recipientConfig?.ccAddresses || [],
        bccAddresses: type.recipientConfig?.bccAddresses || [],
        includeProshopContact: type.recipientConfig?.includeProshopContact || false,
        includeTournamentPlayers: type.recipientConfig?.includeTournamentPlayers || false,
        includeWaitlist: type.recipientConfig?.includeWaitlist || false,
        includeAdminContact: type.recipientConfig?.includeAdminContact || false
      }
    });
    setEditMode(true);
    setCreateMode(false);
    setCascadePreview(null);

    try {
      const statsRes = await scheduledEmailTypeAPI.getStats(type._id);
      setSelectedType(prev => ({ ...prev, stats: statsRes.data }));
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handlePreviewCascade = async () => {
    if (!selectedType) return;

    try {
      const preview = await scheduledEmailTypeAPI.previewCascade(selectedType._id, {
        offsetDays: formData.offsetDays,
        sendTime: formData.sendTime,
        templateId: formData.templateId
      });
      setCascadePreview(preview.data);
    } catch (err) {
      console.error('Error previewing cascade:', err);
      setError('Failed to preview cascade changes');
    }
  };

  const handleSave = async (cascade = false) => {
    if (!selectedType) return;

    try {
      setSaving(true);
      setError(null);

      const updateData = {
        name: formData.name,
        offsetDays: parseInt(formData.offsetDays, 10),
        sendTime: formData.sendTime,
        templateId: formData.templateId,
        description: formData.description,
        isActive: formData.isActive,
        recipientConfig: formData.recipientConfig
      };

      if (cascade) {
        await scheduledEmailTypeAPI.updateAndCascade(selectedType._id, updateData);
      } else {
        await scheduledEmailTypeAPI.update(selectedType._id, updateData);
      }

      await loadTypesData();
      handleCancelEdit();
    } catch (err) {
      console.error('Error saving email type:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setCreateMode(false);
    setSelectedType(null);
    setCascadePreview(null);
    setFormData({
      code: '',
      name: '',
      category: 'member',
      offsetDays: 0,
      sendTime: '09:00',
      templateId: '',
      description: '',
      isActive: true,
      recipientConfig: {
        toAddresses: [],
        ccAddresses: [],
        bccAddresses: [],
        includeProshopContact: false,
        includeTournamentPlayers: false,
        includeWaitlist: false,
        includeAdminContact: false
      }
    });
  };

  const handleCreateClick = () => {
    setCreateMode(true);
    setEditMode(false);
    setSelectedType(null);
    setFormData({
      code: '',
      name: '',
      category: 'member',
      offsetDays: 0,
      sendTime: '09:00',
      templateId: '',
      description: '',
      isActive: true,
      recipientConfig: {
        toAddresses: [],
        ccAddresses: [],
        bccAddresses: [],
        includeProshopContact: false,
        includeTournamentPlayers: false,
        includeWaitlist: false,
        includeAdminContact: false
      }
    });
  };

  // Handle column header click for sorting in list view
  const handleListSort = (field) => {
    setListSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }));
    // Reset to page 1 when sort changes - the useEffect will trigger refetch
    setListPagination(prev => ({ ...prev, page: 1 }));
  };

  // Render sort indicator arrow
  const renderSortArrow = (field) => {
    if (listSort.field !== field) return <span className="ml-1 text-slate-300">↕</span>;
    return <span className="ml-1">{listSort.order === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);

      const createData = {
        code: formData.code,
        name: formData.name,
        category: formData.category,
        offsetDays: parseInt(formData.offsetDays, 10),
        sendTime: formData.sendTime,
        templateId: formData.templateId || undefined,
        description: formData.description,
        isActive: formData.isActive,
        recipientConfig: formData.recipientConfig
      };

      await scheduledEmailTypeAPI.create(createData);
      await loadTypesData();
      handleCancelEdit();
    } catch (err) {
      console.error('Error creating email type:', err);
      setError(err.response?.data?.message || 'Failed to create email type');
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation modal for email type
  const handleDeleteClick = (type) => {
    setTypeToDelete(type);
    setShowDeleteModal(true);
  };

  // Soft delete (deactivate) email type
  const handleSoftDelete = async () => {
    if (!typeToDelete) return;

    try {
      setDeleteLoading(true);
      setError(null);
      await scheduledEmailTypeAPI.delete(typeToDelete._id);
      await loadTypesData();
      setShowDeleteModal(false);
      setTypeToDelete(null);
      handleCancelEdit();
    } catch (err) {
      console.error('Error deactivating email type:', err);
      setError(err.response?.data?.message || 'Failed to deactivate email type');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Hard delete (permanent - removes type and all notifications)
  const handlePermanentDelete = async () => {
    if (!typeToDelete) return;

    try {
      setDeleteLoading(true);
      setError(null);
      await scheduledEmailTypeAPI.permanentDelete(typeToDelete._id);
      await loadTypesData();
      setShowDeleteModal(false);
      setTypeToDelete(null);
      handleCancelEdit();
    } catch (err) {
      console.error('Error deleting email type:', err);
      setError(err.response?.data?.message || 'Failed to delete email type');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTypeToDelete(null);
  };

  // Load notifications for the selected email type
  const loadNotifications = useCallback(async (typeId) => {
    if (!typeId) return;

    try {
      setNotificationsLoading(true);
      const response = await scheduledEmailTypeAPI.getNotifications(typeId, {
        status: notificationFilter === 'all' ? undefined : notificationFilter,
        limit: 50
      });
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setNotificationsLoading(false);
    }
  }, [notificationFilter]);

  // Load notifications when selected type changes or filter changes
  useEffect(() => {
    if (editMode && selectedType?._id) {
      loadNotifications(selectedType._id);
    } else {
      setNotifications([]);
    }
  }, [editMode, selectedType?._id, loadNotifications]);

  // Skip a notification
  const handleSkipNotification = async (notificationId, reason = 'Manually skipped') => {
    try {
      await scheduledEmailTypeAPI.skipNotification(notificationId, reason, 'admin');
      await loadNotifications(selectedType._id);
    } catch (err) {
      console.error('Error skipping notification:', err);
      setError(err.response?.data?.message || 'Failed to skip notification');
    }
  };

  // Unskip (re-enable) a notification
  const handleUnskipNotification = async (notificationId) => {
    try {
      await scheduledEmailTypeAPI.unskipNotification(notificationId, 'admin');
      await loadNotifications(selectedType._id);
    } catch (err) {
      console.error('Error unskipping notification:', err);
      setError(err.response?.data?.message || 'Failed to re-enable notification');
    }
  };

  // Delete a notification permanently (for Email Types panel)
  const handleDeleteNotification = async (notificationId, eventName) => {
    if (!window.confirm(`Are you sure you want to permanently delete this notification for "${eventName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await scheduledEmailTypeAPI.deleteNotification(notificationId);
      await loadNotifications(selectedType._id);
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(err.response?.data?.message || 'Failed to delete notification');
    }
  };

  // Resend a sent notification (archives original and creates new clone)
  const handleResendNotification = async (notification) => {
    const sendNow = window.confirm(
      `This will archive the sent notification and create a new one for "${notification.eventName}".\n\nClick OK to send immediately, or Cancel to schedule it as pending.`
    );

    try {
      const result = await scheduledEmailTypeAPI.archiveAndResend(notification._id, {
        reason: 'Resending notification',
        sendImmediately: sendNow
      });

      // Refresh the list
      await fetchAllNotifications(listPagination.page);

      const action = sendNow ? 'sent' : 'scheduled for resend';
      alert(`Success! Notification ${action}. New notification ID: ${result.data.clone._id}`);
    } catch (err) {
      console.error('Error resending notification:', err);
      setError(err.response?.data?.message || 'Failed to resend notification');
    }
  };

  // Send a pending notification immediately
  const handleSendNow = async (notification) => {
    if (!window.confirm(`Send notification for "${notification.eventName}" immediately?`)) {
      return;
    }

    try {
      await scheduledEmailTypeAPI.sendNow(notification._id);
      await fetchAllNotifications(listPagination.page);
      alert('Notification sent successfully!');
    } catch (err) {
      console.error('Error sending notification:', err);
      setError(err.response?.data?.message || 'Failed to send notification');
    }
  };

  // Regenerate notifications for this email type
  const handleRegenerate = async () => {
    if (!selectedType?._id) return;

    try {
      setRegenerating(true);
      const result = await scheduledEmailTypeAPI.regenerate(selectedType._id, {
        futureOnly: true,
        preserveOverridden: true
      });

      await loadNotifications(selectedType._id);
      const statsRes = await scheduledEmailTypeAPI.getStats(selectedType._id);
      setSelectedType(prev => ({ ...prev, stats: statsRes.data }));

      setError(null);
      alert(`Regenerated: ${result.data.createdCount} created, ${result.data.deletedCount} deleted`);
    } catch (err) {
      console.error('Error regenerating notifications:', err);
      setError(err.response?.data?.message || 'Failed to regenerate notifications');
    } finally {
      setRegenerating(false);
    }
  };

  // Load available events for the selected email type
  const loadAvailableEvents = async () => {
    if (!selectedType?._id) return;

    try {
      setLoadingEvents(true);
      setShowEventSelector(true);
      setEventTypeFilter('all');
      const response = await scheduledEmailTypeAPI.getAvailableEvents(selectedType._id, {
        futureOnly: true
      });
      setAvailableEvents(response.data.events || []);
      setSelectedEventIds([]);
    } catch (err) {
      console.error('Error loading available events:', err);
      setError(err.response?.data?.message || 'Failed to load available events');
    } finally {
      setLoadingEvents(false);
    }
  };

  // Generate notifications for selected events
  const handleGenerateForEvents = async () => {
    if (!selectedType?._id || selectedEventIds.length === 0) return;

    try {
      setGeneratingEvents(true);
      const result = await scheduledEmailTypeAPI.generateForEvents(
        selectedType._id,
        selectedEventIds,
        { replaceExisting: false }
      );

      await loadNotifications(selectedType._id);
      const statsRes = await scheduledEmailTypeAPI.getStats(selectedType._id);
      setSelectedType(prev => ({ ...prev, stats: statsRes.data }));

      setGenerationResults({
        createdCount: result.data.createdCount,
        skippedCount: result.data.skippedCount,
        errorCount: result.data.errorCount || 0,
        created: result.data.created || [],
        skipped: result.data.skipped || [],
        errors: result.data.errors || []
      });

      setShowEventSelector(false);
      setSelectedEventIds([]);
      setAvailableEvents([]);
    } catch (err) {
      console.error('Error generating notifications:', err);
      setError(err.response?.data?.message || 'Failed to generate notifications');
    } finally {
      setGeneratingEvents(false);
    }
  };

  // Toggle event selection
  const toggleEventSelection = (eventId) => {
    setSelectedEventIds(prev =>
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Helper to check if event's scheduled notification date is in the past
  const isEventScheduledInPast = (event) => {
    if (!event.scheduledFor) return false;
    return new Date(event.scheduledFor) < new Date();
  };

  // Get selectable events (exclude past-dated events)
  const selectableEvents = filteredEvents.filter(e => !isEventScheduledInPast(e));

  // Select/deselect all filtered events (excluding past-dated ones)
  const toggleSelectAllEvents = () => {
    const selectableIds = selectableEvents.map(e => e._id);
    const allSelectableSelected = selectableIds.every(id => selectedEventIds.includes(id));

    if (allSelectableSelected) {
      setSelectedEventIds(prev => prev.filter(id => !selectableIds.includes(id)));
    } else {
      setSelectedEventIds(prev => [...new Set([...prev, ...selectableIds])]);
    }
  };

  // Helper functions
  const formatOffsetDays = (days) => {
    if (days === 0) return 'Day of event';
    if (days < 0) return `${Math.abs(days)} days before`;
    return `${days} days after`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      proshop: 'bg-blue-100 text-blue-800',
      member: 'bg-green-100 text-green-800',
      reminder: 'bg-amber-100 text-amber-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      skipped: 'bg-gray-100 text-gray-600',
      processing: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-600 hover:text-slate-900"
              >
                ← Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <span>📧</span>
                  Email Management
                </h1>
                <p className="text-sm text-slate-600">
                  Manage scheduled notifications and email type blueprints
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'types' && !createMode && !editMode && (
                <Button onClick={handleCreateClick}>
                  + New Email Type
                </Button>
              )}
              {activeTab === 'types' && (createMode || editMode) && (
                <Button variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/email-activity')}
              >
                View Email Activity
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/communication')}
              >
                Templates
              </Button>
            </div>
          </div>

          {/* Main Tab Navigation - All tabs at same level */}
          <div className="flex items-center gap-1 mt-4 border-t pt-4">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              📊 Dashboard
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'list'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              📋 All Notifications
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'calendar'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              📅 Calendar
            </button>
            <button
              onClick={() => setActiveTab('types')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'types'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              ⚙️ Email Types
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline text-sm">
              Dismiss
            </button>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <EmailSchedulePanel />
        )}

        {/* All Notifications List Tab */}
        {activeTab === 'list' && (
          <div className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Status Filter Tabs */}
                  <div className="flex items-center bg-slate-100 rounded-lg p-1">
                    {['all', 'pending', 'sent', 'failed', 'skipped'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setListFilters(prev => ({ ...prev, status }))}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          listFilters.status === status
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        {statusCounts[status] !== undefined && (
                          <span className="ml-1 text-xs text-slate-500">({statusCounts[status]})</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Email Type Filter */}
                  <select
                    value={listFilters.emailTypeId}
                    onChange={(e) => setListFilters(prev => ({ ...prev, emailTypeId: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">All Email Types</option>
                    {emailTypes.map((type) => (
                      <option key={type._id} value={type._id}>{type.name}</option>
                    ))}
                  </select>

                  {/* Event Type Filter */}
                  <select
                    value={listFilters.eventType}
                    onChange={(e) => setListFilters(prev => ({ ...prev, eventType: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="">All Event Types</option>
                    {eventTypeOptions.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Event Filter */}
                  <select
                    value={listFilters.eventId}
                    onChange={(e) => setListFilters(prev => ({ ...prev, eventId: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm min-w-[150px]"
                  >
                    <option value="">All Events</option>
                    {uniqueEvents.map((event) => (
                      <option key={event.id || event._id} value={event.id || event._id}>{event.name}</option>
                    ))}
                  </select>

                  {/* Search */}
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={listFilters.search}
                    onChange={(e) => setListFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm w-48"
                  />

                  {/* Refresh Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchAllNotifications(1)}
                    disabled={listLoading}
                  >
                    {listLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notifications Table */}
            <Card>
              <CardContent className="p-0">
                {listLoading && allNotifications.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                    <p className="text-slate-500">Loading notifications...</p>
                  </div>
                ) : allNotifications.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <p className="text-lg font-medium">No notifications found</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th
                            className="text-left px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => handleListSort('eventName')}
                          >
                            Event{renderSortArrow('eventName')}
                          </th>
                          <th
                            className="text-left px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => handleListSort('emailTypeCode')}
                          >
                            Email Type{renderSortArrow('emailTypeCode')}
                          </th>
                          <th
                            className="text-left px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => handleListSort('scheduledFor')}
                          >
                            Scheduled For{renderSortArrow('scheduledFor')}
                          </th>
                          <th
                            className="text-left px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => handleListSort('status')}
                          >
                            Status{renderSortArrow('status')}
                          </th>
                          <th
                            className="text-left px-4 py-3 text-sm font-medium text-slate-600 cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => handleListSort('recipientCount')}
                          >
                            Recipients{renderSortArrow('recipientCount')}
                          </th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {allNotifications.map((notification) => (
                          <tr key={notification._id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{notification.eventName || 'Unknown Event'}</div>
                              <div className="text-sm text-slate-500">
                                {notification.eventDate ? new Date(notification.eventDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : ''}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="text-xs">
                                {notification.emailTypeCode || notification.notificationType || 'N/A'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {notification.scheduledFor ? (
                                <>
                                  <div>{new Date(notification.scheduledFor).toLocaleDateString()}</div>
                                  <div className="text-slate-400">{new Date(notification.scheduledFor).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                </>
                              ) : 'Not scheduled'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={getStatusColor(notification.status)}>
                                {notification.status}
                              </Badge>
                              {notification.hasOverride && (
                                <Badge variant="outline" className="ml-1 text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  Override
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {notification.recipientCount || notification.recipients?.length || 0}
                            </td>
                            <td className="px-4 py-3 text-right space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewNotification(notification)}
                              >
                                View
                              </Button>
                              {notification.status === 'sent' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleResendNotification(notification)}
                                >
                                  Resend
                                </Button>
                              )}
                              {notification.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleSendNow(notification)}
                                >
                                  Send Now
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {listPagination.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                    <div className="text-sm text-slate-600">
                      Showing {((listPagination.page - 1) * 25) + 1} to {Math.min(listPagination.page * 25, listPagination.total)} of {listPagination.total}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAllNotifications(listPagination.page - 1)}
                        disabled={listPagination.page <= 1 || listLoading}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600">
                        Page {listPagination.page} of {listPagination.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAllNotifications(listPagination.page + 1)}
                        disabled={listPagination.page >= listPagination.totalPages || listLoading}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-4">
            {/* Calendar Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-white">
                  {pendingNotifications.length} Pending
                </Badge>
                <Badge variant="outline" className="bg-white">
                  {notificationHistory.length} Sent
                </Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchCalendarData}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </div>

            {loading && !pendingNotifications.length ? (
              <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-slate-500">Loading calendar...</p>
              </div>
            ) : (
              <EmailCalendarView
                pendingNotifications={pendingNotifications}
                notificationHistory={notificationHistory}
                onPreview={handlePreview}
                onSkip={handleSkip}
                onUnskip={handleUnskip}
                onTrigger={handleTrigger}
                onRefresh={fetchCalendarData}
              />
            )}
          </div>
        )}

        {/* Email Types Tab */}
        {activeTab === 'types' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Email Types List */}
            <div className={`${editMode || createMode ? 'lg:col-span-1' : 'lg:col-span-3'}`}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Email Types ({emailTypes.length})</span>
                    <Button variant="ghost" size="sm" onClick={loadTypesData} disabled={typesLoading}>
                      <svg className={`w-4 h-4 ${typesLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {typesLoading ? (
                    <div className="space-y-3">
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  ) : emailTypes.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-lg font-medium">No email types configured</p>
                      <p className="text-sm mt-1">Create your first email type to get started</p>
                      <Button className="mt-4" onClick={handleCreateClick}>
                        Create Email Type
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {emailTypes.map((type) => (
                        <div
                          key={type._id}
                          className={`p-4 border rounded-lg transition-all cursor-pointer ${
                            selectedType?._id === type._id
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'hover:border-blue-300 bg-white'
                          } ${!type.isActive ? 'opacity-60' : ''}`}
                          onClick={() => handleEditClick(type)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-medium truncate">{type.name}</span>
                                <Badge className={getCategoryColor(type.category)}>
                                  {type.category}
                                </Badge>
                                {!type.isActive && (
                                  <Badge variant="secondary">Inactive</Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 font-mono mb-2">
                                {type.code}
                              </p>
                              {type.description && (
                                <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                  {type.description}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                                <span>{formatOffsetDays(type.offsetDays)}</span>
                                <span>@ {type.sendTime}</span>
                                <span>{type.templateId?.name || 'No template'}</span>
                              </div>
                            </div>
                            <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Edit/Create Panel */}
            {(editMode || createMode) && (
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {createMode ? 'Create New Email Type' : `Edit: ${selectedType?.name}`}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Stats Summary (edit mode only) */}
                    {editMode && selectedType?.stats && (
                      <div className="grid grid-cols-4 gap-3">
                        <div className="p-3 bg-gray-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-gray-900">{selectedType.stats.total}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-blue-600">{selectedType.stats.pending}</div>
                          <div className="text-xs text-gray-500">Pending</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-green-600">{selectedType.stats.sent}</div>
                          <div className="text-xs text-gray-500">Sent</div>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-amber-600">{selectedType.stats.overridden}</div>
                          <div className="text-xs text-gray-500">Overridden</div>
                        </div>
                      </div>
                    )}

                    {/* Form */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Code {createMode && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '').replace(/-+/g, '-')
                            }))}
                            placeholder="e.g., tournament-reminder"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!createMode ? 'bg-gray-100 text-gray-600' : ''}`}
                            readOnly={!createMode}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {createMode ? 'Unique identifier (lowercase, hyphens only)' : 'Unique identifier (cannot be changed)'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="member">Member</option>
                            <option value="proshop">Pro Shop</option>
                            <option value="reminder">Reminder</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Tournament Reminder"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Days from Event
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={formData.offsetDays}
                              onChange={(e) => setFormData(prev => ({ ...prev, offsetDays: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-500 whitespace-nowrap">
                              {formatOffsetDays(parseInt(formData.offsetDays, 10) || 0)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Negative = before event, Positive = after</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Send Time
                          </label>
                          <input
                            type="time"
                            value={formData.sendTime}
                            onChange={(e) => setFormData(prev => ({ ...prev, sendTime: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Template
                        </label>
                        <select
                          value={formData.templateId}
                          onChange={(e) => setFormData(prev => ({ ...prev, templateId: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a template...</option>
                          {templates.map((template) => (
                            <option key={template._id} value={template._id}>
                              {template.name} - {template.subject}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          placeholder="Brief description of this email type..."
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Recipients Configuration */}
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          Default Recipients
                        </h4>
                        <p className="text-sm text-gray-500 mb-4">
                          Configure who receives emails of this type by default. Can be overridden on individual notifications.
                        </p>

                        {/* Static Recipients */}
                        <div className="space-y-4">
                          {/* TO Addresses */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              To Recipients
                            </label>
                            <div className="space-y-2">
                              {(formData.recipientConfig?.toAddresses || []).map((addr, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={addr.name || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.toAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, toAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="Name (optional)"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <input
                                    type="email"
                                    value={addr.email || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.toAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, toAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="email@example.com"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAddresses = (formData.recipientConfig?.toAddresses || []).filter((_, i) => i !== index);
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, toAddresses: newAddresses }
                                      }));
                                    }}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const newAddresses = [...(formData.recipientConfig?.toAddresses || []), { name: '', email: '' }];
                                  setFormData(prev => ({
                                    ...prev,
                                    recipientConfig: { ...prev.recipientConfig, toAddresses: newAddresses }
                                  }));
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add To Recipient
                              </button>
                            </div>
                          </div>

                          {/* CC Addresses */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              CC Recipients
                            </label>
                            <div className="space-y-2">
                              {(formData.recipientConfig?.ccAddresses || []).map((addr, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={addr.name || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.ccAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, ccAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="Name (optional)"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <input
                                    type="email"
                                    value={addr.email || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.ccAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, ccAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="email@example.com"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAddresses = (formData.recipientConfig?.ccAddresses || []).filter((_, i) => i !== index);
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, ccAddresses: newAddresses }
                                      }));
                                    }}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const newAddresses = [...(formData.recipientConfig?.ccAddresses || []), { name: '', email: '' }];
                                  setFormData(prev => ({
                                    ...prev,
                                    recipientConfig: { ...prev.recipientConfig, ccAddresses: newAddresses }
                                  }));
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add CC Recipient
                              </button>
                            </div>
                          </div>

                          {/* BCC Addresses */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              BCC Recipients
                            </label>
                            <div className="space-y-2">
                              {(formData.recipientConfig?.bccAddresses || []).map((addr, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={addr.name || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.bccAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], name: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, bccAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="Name (optional)"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <input
                                    type="email"
                                    value={addr.email || ''}
                                    onChange={(e) => {
                                      const newAddresses = [...(formData.recipientConfig?.bccAddresses || [])];
                                      newAddresses[index] = { ...newAddresses[index], email: e.target.value };
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, bccAddresses: newAddresses }
                                      }));
                                    }}
                                    placeholder="email@example.com"
                                    className="flex-1 px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAddresses = (formData.recipientConfig?.bccAddresses || []).filter((_, i) => i !== index);
                                      setFormData(prev => ({
                                        ...prev,
                                        recipientConfig: { ...prev.recipientConfig, bccAddresses: newAddresses }
                                      }));
                                    }}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const newAddresses = [...(formData.recipientConfig?.bccAddresses || []), { name: '', email: '' }];
                                  setFormData(prev => ({
                                    ...prev,
                                    recipientConfig: { ...prev.recipientConfig, bccAddresses: newAddresses }
                                  }));
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Add BCC Recipient
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Recipients */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Dynamic Recipients (resolved at send time)
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.recipientConfig?.includeProshopContact || false}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  recipientConfig: { ...prev.recipientConfig, includeProshopContact: e.target.checked }
                                }))}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              Include Proshop Contact
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.recipientConfig?.includeTournamentPlayers || false}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  recipientConfig: { ...prev.recipientConfig, includeTournamentPlayers: e.target.checked }
                                }))}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              Include Tournament Players
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.recipientConfig?.includeWaitlist || false}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  recipientConfig: { ...prev.recipientConfig, includeWaitlist: e.target.checked }
                                }))}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              Include Waitlist
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.recipientConfig?.includeAdminContact || false}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  recipientConfig: { ...prev.recipientConfig, includeAdminContact: e.target.checked }
                                }))}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              Include Admin Contact
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={formData.isActive}
                          onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <label htmlFor="isActive" className="text-sm text-gray-700">
                          Active (will be used for new events)
                        </label>
                      </div>
                    </div>

                    {/* Cascade Preview (edit mode only) */}
                    {editMode && cascadePreview && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="font-medium mb-3">Cascade Preview</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline">Changed Fields</Badge>
                            {cascadePreview.changedFields?.length > 0 ? (
                              cascadePreview.changedFields.map(f => (
                                <Badge key={f} className="bg-blue-100 text-blue-800">{f}</Badge>
                              ))
                            ) : (
                              <span className="text-gray-500">No changes detected</span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-3">
                            <div className="p-3 bg-green-50 rounded">
                              <div className="font-medium text-green-700">
                                {cascadePreview.willUpdate} notifications will be updated
                              </div>
                            </div>
                            <div className="p-3 bg-amber-50 rounded">
                              <div className="font-medium text-amber-700">
                                {cascadePreview.willSkip} notifications will be skipped
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        {editMode && (
                          <Button
                            variant="outline"
                            onClick={() => handleDeleteClick(selectedType)}
                            className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {editMode && (
                          <>
                            <Button
                              variant="outline"
                              onClick={handlePreviewCascade}
                              disabled={saving}
                            >
                              Preview Cascade
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleSave(false)}
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save Only'}
                            </Button>
                            <Button
                              onClick={() => handleSave(true)}
                              disabled={saving}
                            >
                              {saving ? 'Saving...' : 'Save & Cascade'}
                            </Button>
                          </>
                        )}
                        {createMode && (
                          <Button
                            onClick={handleCreate}
                            disabled={saving || !formData.code || !formData.name}
                          >
                            {saving ? 'Creating...' : 'Create Email Type'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Notification Viewer Panel (edit mode only) */}
                {editMode && showNotificationPanel && (
                  <Card className="mt-6">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Scheduled Notifications</CardTitle>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={loadAvailableEvents}
                            disabled={loadingEvents}
                          >
                            {loadingEvents ? 'Loading...' : '+ Generate for Events'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRegenerate}
                            disabled={regenerating}
                          >
                            {regenerating ? 'Regenerating...' : 'Regenerate'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowNotificationPanel(false)}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Event Selector Panel */}
                      {showEventSelector && (
                        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-blue-900">
                              Select Events to Generate Notifications
                            </h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowEventSelector(false);
                                setAvailableEvents([]);
                                setSelectedEventIds([]);
                              }}
                            >
                              ✕
                            </Button>
                          </div>

                          {loadingEvents ? (
                            <div className="text-center py-4">
                              <p className="text-sm text-blue-700">Loading available events...</p>
                            </div>
                          ) : availableEvents.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">
                              <p>No available events found for this email type.</p>
                            </div>
                          ) : (
                            <>
                              {/* Event Type Filter */}
                              <div className="flex flex-wrap items-center gap-1 mb-3 pb-2 border-b border-blue-200">
                                {eventTypeOptions.map((option) => {
                                  const count = option.value === 'all'
                                    ? availableEvents.length
                                    : availableEvents.filter(e => e.eventType === option.value).length;
                                  if (count === 0 && option.value !== 'all') return null;
                                  return (
                                    <button
                                      key={option.value}
                                      onClick={() => setEventTypeFilter(option.value)}
                                      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                                        eventTypeFilter === option.value
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white text-blue-700 hover:bg-blue-100 border border-blue-200'
                                      }`}
                                    >
                                      {option.label} ({count})
                                    </button>
                                  );
                                })}
                              </div>

                              <div className="flex items-center justify-between mb-2">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={filteredEvents.length > 0 && filteredEvents.every(e => selectedEventIds.includes(e._id))}
                                    onChange={toggleSelectAllEvents}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600"
                                  />
                                  <span className="font-medium">
                                    Select All ({filteredEvents.length} events)
                                  </span>
                                </label>
                                <span className="text-sm text-blue-700">
                                  {selectedEventIds.length} selected
                                </span>
                              </div>

                              <div className="max-h-48 overflow-y-auto border border-blue-100 rounded bg-white">
                                {filteredEvents.map((event) => {
                                  const isPastScheduled = isEventScheduledInPast(event);
                                  return (
                                    <label
                                      key={event._id}
                                      className={`flex items-center gap-3 p-2 border-b border-blue-50 last:border-b-0 ${
                                        isPastScheduled
                                          ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                          : 'hover:bg-blue-50 cursor-pointer'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedEventIds.includes(event._id)}
                                        onChange={() => !isPastScheduled && toggleEventSelection(event._id)}
                                        disabled={isPastScheduled}
                                        className="w-4 h-4 rounded border-gray-300"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm truncate">
                                          {event.name}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {new Date(event.eventDate).toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            timeZone: 'UTC'
                                          })}
                                          {isPastScheduled && (
                                            <span className="ml-2 text-red-500 font-medium">(scheduled date passed)</span>
                                          )}
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>

                              <div className="flex items-center justify-end gap-2 mt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowEventSelector(false);
                                    setAvailableEvents([]);
                                    setSelectedEventIds([]);
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={handleGenerateForEvents}
                                  disabled={selectedEventIds.length === 0 || generatingEvents}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  {generatingEvents ? 'Generating...' : `Generate ${selectedEventIds.length} Notification${selectedEventIds.length !== 1 ? 's' : ''}`}
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Generation Results Display */}
                      {generationResults && (
                        <div className="mb-4 rounded-lg border overflow-hidden">
                          <div className={`px-4 py-3 flex items-center justify-between ${
                            generationResults.errorCount > 0
                              ? 'bg-red-50 border-b border-red-200'
                              : generationResults.skippedCount > 0
                                ? 'bg-amber-50 border-b border-amber-200'
                                : 'bg-green-50 border-b border-green-200'
                          }`}>
                            <div>
                              <span className="font-medium">Generation Complete</span>
                              <span className="mx-2 text-gray-400">|</span>
                              <span className="text-green-700">{generationResults.createdCount} created</span>
                              {generationResults.skippedCount > 0 && (
                                <span className="text-amber-700 ml-2">{generationResults.skippedCount} skipped</span>
                              )}
                              {generationResults.errorCount > 0 && (
                                <span className="text-red-700 ml-2">{generationResults.errorCount} errors</span>
                              )}
                            </div>
                            <button onClick={() => setGenerationResults(null)} className="text-gray-500 hover:text-gray-700">
                              ✕
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Filter Tabs */}
                      <div className="flex items-center gap-2 mb-4 border-b pb-3">
                        {['all', 'pending', 'sent', 'skipped', 'failed'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => setNotificationFilter(filter)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              notificationFilter === filter
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                          </button>
                        ))}
                      </div>

                      {/* Notifications List */}
                      {notificationsLoading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No {notificationFilter === 'all' ? '' : notificationFilter} notifications found</p>
                          {notificationFilter !== 'all' && (
                            <button
                              onClick={() => setNotificationFilter('all')}
                              className="text-blue-600 text-sm mt-1 hover:underline"
                            >
                              View all notifications
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                          {notifications.map((notification) => (
                            <div
                              key={notification._id}
                              className={`flex items-center justify-between p-3 border rounded-lg ${
                                notification.status === 'skipped' ? 'bg-gray-50' : 'bg-white'
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm truncate">
                                    {notification.eventName || 'Unknown Event'}
                                  </span>
                                  <Badge className={getStatusColor(notification.status)}>
                                    {notification.status}
                                  </Badge>
                                  {notification.hasOverride && (
                                    <Badge variant="outline" className="text-xs">
                                      Override
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <span>
                                    {notification.scheduledFor
                                      ? new Date(notification.scheduledFor).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit'
                                        })
                                      : 'Not scheduled'
                                    }
                                  </span>
                                  {notification.eventDate && (
                                    <span>
                                      Event: {new Date(notification.eventDate).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        timeZone: 'UTC'
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                {notification.status === 'pending' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleSkipNotification(notification._id)}
                                    className="text-gray-500 hover:text-red-600"
                                  >
                                    Skip
                                  </Button>
                                )}
                                {notification.status === 'skipped' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUnskipNotification(notification._id)}
                                    className="text-gray-500 hover:text-green-600"
                                  >
                                    Restore
                                  </Button>
                                )}
                                {['pending', 'skipped', 'failed'].includes(notification.status) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteNotification(notification._id, notification.eventName)}
                                    className="text-gray-500 hover:text-red-600"
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Summary Footer */}
                      {notifications.length > 0 && (
                        <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm text-gray-500">
                          <span>
                            Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadNotifications(selectedType._id)}
                            disabled={notificationsLoading}
                          >
                            Refresh
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Toggle to show notification panel if hidden */}
                {editMode && !showNotificationPanel && (
                  <button
                    onClick={() => setShowNotificationPanel(true)}
                    className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
                  >
                    Show Notifications Panel
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && typeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Email Type</h3>
                  <p className="text-sm text-gray-500">{typeToDelete.name}</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Choose how you want to handle this email type:
                </p>

                {/* Stats about what will be affected */}
                {typeToDelete.stats && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                    <div className="font-medium text-gray-700 mb-2">This type has:</div>
                    <div className="grid grid-cols-2 gap-2 text-gray-600">
                      <div>• {typeToDelete.stats.total || 0} total notifications</div>
                      <div>• {typeToDelete.stats.pending || 0} pending</div>
                      <div>• {typeToDelete.stats.sent || 0} sent</div>
                      <div>• {typeToDelete.stats.overridden || 0} with overrides</div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {/* Deactivate option */}
                  <div className="border rounded-lg p-4 hover:border-amber-300 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Deactivate</div>
                        <p className="text-sm text-gray-500 mt-1">
                          Hides from new events but keeps all existing notifications and history. Can be reactivated later.
                        </p>
                        <Button
                          onClick={handleSoftDelete}
                          disabled={deleteLoading}
                          className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          {deleteLoading ? 'Processing...' : 'Deactivate Type'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Permanent delete option */}
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-red-900">Delete Permanently</div>
                        <p className="text-sm text-red-700 mt-1">
                          <strong>Warning:</strong> This will permanently delete the email type AND all {typeToDelete.stats?.total || 0} associated notifications. This cannot be undone.
                        </p>
                        <Button
                          onClick={handlePermanentDelete}
                          disabled={deleteLoading}
                          className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                        >
                          {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleCloseDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledEmails;
